/**
 * NavSection Component
 *
 * Раскрывающаяся секция навигационного меню портала
 * - Управляет состоянием открытия/закрытия подпунктов
 * - Поддержка collapsed режима
 * - Анимация раскрытия
 * - Иконка-стрелка для индикации состояния
 */

import React, { useState } from 'react';
import { Box, Group, Text, Stack, Tooltip, Collapse } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import tokens from '../theme/designTokens';

const NavSection = ({
  icon,           // Иконка секции (например, <IconBuilding />)
  label,          // Название секции (например, "Sales")
  children,       // Подпункты меню (NavItem компоненты)
  collapsed,      // Collapsed режим sidebar
  defaultOpen = true  // По умолчанию раскрыта
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // В collapsed режиме показываем только иконку с tooltip
  if (collapsed) {
    return (
      <Tooltip label={label} position="right" withArrow>
        <Box
          style={{
            padding: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            color: tokens.colors.neutral[7],
          }}
        >
          {icon}
        </Box>
      </Tooltip>
    );
  }

  // В expanded режиме показываем полную секцию с раскрытием
  return (
    <Box mt="md">
      {/* Заголовок секции с кликабельной областью */}
      <Box
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
          userSelect: 'none',
          borderRadius: tokens.borders.radius.sm,
          margin: '0 4px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = tokens.colors.neutral[2];
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Group gap="sm" wrap="nowrap">
          {/* Иконка секции */}
          {icon && (
            <Box style={{
              color: tokens.colors.neutral[7],
              display: 'flex',
              alignItems: 'center'
            }}>
              {icon}
            </Box>
          )}

          {/* Название секции */}
          <Text
            size="xs"
            fw={700}
            c={tokens.colors.neutral[7]}
            tt="uppercase"
            style={{
              letterSpacing: '0.5px',
              flex: 1
            }}
          >
            {label}
          </Text>

          {/* Стрелка раскрытия */}
          <Box
            style={{
              color: tokens.colors.neutral[6],
              display: 'flex',
              alignItems: 'center',
              transition: 'transform 0.3s ease, color 0.2s ease',
              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            }}
          >
            <IconChevronDown size={16} />
          </Box>
        </Group>
      </Box>

      {/* Раскрывающийся список подпунктов */}
      <Collapse in={isOpen} transitionDuration={300} transitionTimingFunction="ease">
        <Stack gap={0} mt="xs">
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default NavSection;
